import { useState, useEffect } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { BsLink45Deg, BsQrCode, BsClipboard, BsFolder2Open, BsClock, BsFilter } from "react-icons/bs";
import { FaRegTimesCircle } from "react-icons/fa";
import "../styles/Dashboard.css";

function Dashboard({ token }) {
  const [url, setUrl] = useState("");
  const [expiryMinutes, setExpiryMinutes] = useState("");
  const [shortUrl, setShortUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [links, setLinks] = useState([]);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrLink, setQrLink] = useState("");
  const [generateType, setGenerateType] = useState("both");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const handleTokenExpiry = () => {
    alert("⚠️ Session expired. Please log in again.");
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  const fetchLinks = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.get("https://chotu-link.vercel.app/mylinks", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLinks(res.data.links || []);
    } catch (err) {
      if (err.response?.status === 401) handleTokenExpiry();
      else console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(
        "https://chotu-link.vercel.app/shorten",
        { url, expiryMinutes: expiryMinutes || undefined, option: generateType },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      setShortUrl(res.data.shortUrl || "");
      setExpiresAt(res.data.expiresAt || "");
      setQrLink(generateType !== "link" ? res.data.shortUrl || url : "");

      setUrl("");
      setExpiryMinutes("");
      fetchLinks();

      setToastMessage("✅ Link generated successfully!");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    } catch (err) {
      if (err.response?.status === 401) handleTokenExpiry();
      else alert(err.response?.data?.error || "Shortening failed");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setToastMessage("✅ Copied to clipboard!");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  const filteredLinks = links
    .filter((link) => {
      const isExpired = link.expires_at && new Date(link.expires_at) < new Date();
      if (filter === "active") return !isExpired;
      if (filter === "expired") return isExpired;
      return true;
    })
    .filter((link) => link.original_url.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="dashboard-container">
      {/* Shorten Form */}
      <div className="premium-card shadow-lg p-4">
        <h2 className="mb-3 fw-bold text-gradient">
          <BsLink45Deg className="me-2" /> Shorten Your URL
        </h2>

        <form onSubmit={handleSubmit} className="d-flex flex-wrap gap-2 align-items-center">
          <input
            type="url"
            placeholder="Paste your long URL..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="form-control rounded-pill"
            required
          />

          {token && (
            <input
              type="number"
              min="1"
              placeholder="Expiry (minutes, optional)"
              value={expiryMinutes}
              onChange={(e) => setExpiryMinutes(e.target.value)}
              className="form-control rounded-pill"
              title="Optional: Set expiry in minutes"
            />
          )}

          <select
            value={generateType}
            onChange={(e) => setGenerateType(e.target.value)}
            className="form-select rounded-pill"
            title="Choose what to generate"
          >
            <option value="both">Short Link + QR</option>
            <option value="link">Short Link Only</option>
            <option value="qr">QR Code Only</option>
          </select>

          <button type="submit" className="btn btn-premium rounded-pill px-4">
            Shorten
          </button>
        </form>

        {(shortUrl || qrLink) && (
          <div className="alert alert-success mt-4 d-flex flex-wrap justify-content-between align-items-center">
            <div>
              {shortUrl && (
                <>
                  Your short link:{" "}
                  <a href={shortUrl} target="_blank" rel="noopener noreferrer">
                    {shortUrl}
                  </a>
                  {expiresAt && (
                    <div className="small text-muted mt-1">
                      <BsClock className="me-1" /> Expires at: {new Date(expiresAt).toLocaleString()}
                    </div>
                  )}
                </>
              )}
              {qrLink && (
                <div className="mt-3">
                  <button
                    className="btn btn-sm btn-outline-dark rounded-pill"
                    data-bs-toggle="modal"
                    data-bs-target="#qrModal"
                    onClick={() => setQrLink(qrLink)}
                    title="View QR Code"
                  >
                    <BsQrCode /> QR
                  </button>
                </div>
              )}
            </div>

            {shortUrl && (
              <div className="d-flex gap-2 mt-2 mt-md-0">
                <button
                  className="btn btn-sm btn-outline-light rounded-pill"
                  onClick={() => copyToClipboard(shortUrl)}
                  title="Copy short link"
                >
                  <BsClipboard /> Copy
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Links List */}
      {token && (
        <div className="mt-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="fw-bold d-flex align-items-center">
              <BsFolder2Open className="me-2" /> Your Links
            </h3>

            <div className="d-flex gap-2">
              <input
                type="text"
                placeholder="Search links..."
                className="form-control search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <div className="dropdown">
                <button className="btn btn-sm btn-outline-secondary rounded-circle" type="button" data-bs-toggle="dropdown">
                  <BsFilter />
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li><button className={`dropdown-item ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>All Links</button></li>
                  <li><button className={`dropdown-item ${filter === "active" ? "active" : ""}`} onClick={() => setFilter("active")}>Active</button></li>
                  <li><button className={`dropdown-item ${filter === "expired" ? "active" : ""}`} onClick={() => setFilter("expired")}>Expired</button></li>
                </ul>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-spinner"></div>
          ) : filteredLinks.length === 0 ? (
            <p className="text-muted">No links found.</p>
          ) : (
            <ul className="list-group premium-list">
              {filteredLinks.map((link) => {
                const shortLink = link.short_url;
                const isExpired = link.expires_at && new Date(link.expires_at) < new Date();
                const isExpiringSoon =
                  !isExpired && link.expires_at && new Date(link.expires_at) - new Date() < 3600000;

                return (
                  <li key={link.short_code} className={`list-group-item d-flex justify-content-between align-items-center flex-wrap ${isExpired ? "expired" : isExpiringSoon ? "expiring-soon" : ""}`}>
                    <div>
                      <a
                        href={isExpired ? "#" : shortLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fw-semibold text-decoration-none short-link"
                        onClick={(e) => isExpired && e.preventDefault()}
                      >
                        {shortLink}
                      </a>
                      <br />
                      <small>{link.original_url}</small>
                      {link.expires_at && (
                        <div className="small mt-1">
                          <BsClock className="me-1" />
                          {isExpired ? <span className="text-danger fw-bold">Expired</span> : <>Expires at: {new Date(link.expires_at).toLocaleString()}</>}
                        </div>
                      )}
                    </div>

                    <div className="d-flex gap-2 align-items-center mt-2 mt-md-0">
                      <span className={`badge rounded-pill ${isExpired ? "bg-secondary" : "bg-primary"}`}>
                        {link.click_count} clicks
                      </span>

                      {!isExpired && (
                        <>
                          <button className="btn btn-sm btn-outline-primary rounded-pill" onClick={() => copyToClipboard(shortLink)} title="Copy link"><BsClipboard /></button>
                          <button className="btn btn-sm btn-outline-dark rounded-pill" data-bs-toggle="modal" data-bs-target="#qrModal" onClick={() => setShortUrl(shortLink)} title="View QR"><BsQrCode /></button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* QR Modal */}
      <div className="modal fade" id="qrModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content text-center p-4">
            <h5 className="fw-bold mb-3"><BsQrCode className="me-2" /> QR Code</h5>
            {qrLink && <QRCodeCanvas value={qrLink} size={220} />}
            <p className="mt-3">
              <a href={qrLink} target="_blank" rel="noopener noreferrer">{qrLink}</a>
            </p>
            <button className="btn btn-secondary mt-2" data-bs-dismiss="modal"><FaRegTimesCircle className="me-1" /> Close</button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {showToast && (
        <div className="toast-container position-fixed bottom-0 end-0 p-3">
          <div className="toast show align-items-center text-bg-success border-0">
            <div className="d-flex">
              <div className="toast-body">{toastMessage}</div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setShowToast(false)}></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
